import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

// Fetch device pictures from S3 with optional date range filtering
export const fetchDevicePictures = async (deviceId, options = {}) => {
  const { startDate, endDate, limit = 100, nextToken } = options;
  
  console.log(`🌐 fetchDevicePictures called for device: ${deviceId}`, { startDate, endDate, limit });
  
  // Build query parameters
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (limit) params.append('limit', limit.toString());
  if (nextToken) params.append('nextToken', nextToken);
  
  const url = `${API_BASE_URL}${API_ENDPOINTS.DEVICES.PICTURES(deviceId)}${params.toString() ? '?' + params.toString() : ''}`;
  console.log(`🌐 API URL: ${url}`);
  
  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    console.log(`📡 Making HTTP request to fetch pictures with headers:`, headers);
    const response = await axios.get(url, { headers });
    console.log(`✅ HTTP response received:`, response.status, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching device pictures:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
};

// Fetch devices for an organization
export const fetchDevices = async (organizationId, options = {}) => {
  try {
    const { limit, page, search, status } = options;
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (limit) params.append('limit', String(limit));
    if (page) params.append('page', String(page));
    if (search) params.append('search', search);
    if (status) params.append('status', status);

    const query = params.toString();
    const url = `${API_BASE_URL}${API_ENDPOINTS.DEVICES.LIST}${query ? `?${query}` : ''}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
};

// Fetch organizations
export const fetchOrganizations = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.LIST}?limit=1000`);
    return response.data;
  } catch (error) {
    console.error('Error fetching organizations:', error);
    throw error;
  }
};

// Fetch single organization by ID
export const fetchOrganizationById = async (organizationId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/organizations/${organizationId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching organization ${organizationId}:`, error);
    throw error;
  }
};

// Fetch debug count status for devices in an organization
export const fetchDebugCountStatus = async (organizationId) => {
  console.log(`🌐 fetchDebugCountStatus called for organization: ${organizationId}`);
  
  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    console.log(`📡 Making HTTP request to fetch debug count status`);
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.DEBUG_COUNT_STATUS(organizationId)}`, { headers });
    console.log(`✅ HTTP response received:`, response.status, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching debug count status:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
};

// Request visual confirmation for all devices in an organization
export const requestVisualConfirmation = async (organizationId) => {
  console.log(`🌐 requestVisualConfirmation called for organization: ${organizationId}`);
  
  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    console.log(`📡 Making HTTP request to request visual confirmation`);
    const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.REQUEST_VISUAL_CONFIRMATION(organizationId)}`, {}, { headers });
    console.log(`✅ HTTP response received:`, response.status, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error requesting visual confirmation:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
};

// Applications API
export const fetchUserApplications = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.APPLICATIONS.LIST}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user applications:', error);
    throw error;
  }
};

export const getSensorEffectiveApplication = async (sensorId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.SENSORS.EFFECTIVE_APPLICATION(sensorId)}`);
    return response.data;
  } catch (error) {
    console.error('Error getting sensor effective application:', error);
    throw error;
  }
};

export const assignSensorApplication = async (sensorId, applicationId, notes = '') => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}${API_ENDPOINTS.SENSORS.ASSIGN_APPLICATION(sensorId)}`,
      { applicationId, notes }
    );
    return response.data;
  } catch (error) {
    console.error('Error assigning sensor application:', error);
    throw error;
  }
};

export const removeSensorApplication = async (sensorId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}${API_ENDPOINTS.SENSORS.APPLICATION(sensorId)}`);
    return response.data;
  } catch (error) {
    console.error('Error removing sensor application:', error);
    throw error;
  }
};

// Fetch device performance data
export const fetchDevicePerformanceData = async (deviceId, startDate, endDate) => {
  console.log(`🌐 fetchDevicePerformanceData called for device: ${deviceId}`);
  console.log(`🌐 Date range: ${startDate} to ${endDate}`);
  
  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Fetch multiple device performance endpoints in parallel
    const [connectivityData, payloadData, batteryData, rssiData, temperatureData, bootReasonData, connectivityPatternData] = await Promise.all([
      axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_CONNECTION_COUNT(deviceId)}?startDate=${startDate}&endDate=${endDate}`, { headers }),
      axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_PAYLOAD_COUNT(deviceId)}?startDate=${startDate}&endDate=${endDate}`, { headers }),
      axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_BATTERY_DAILY(deviceId)}?startDate=${startDate}&endDate=${endDate}`, { headers }),
      axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_RSSI_DAILY(deviceId)}?startDate=${startDate}&endDate=${endDate}`, { headers }),
      axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_TEMPERATURE_DAILY(deviceId)}?startDate=${startDate}&endDate=${endDate}`, { headers }),
      axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_BOOT_REASON(deviceId)}?startDate=${startDate}&endDate=${endDate}`, { headers }),
      axios.get(`${API_BASE_URL}${API_ENDPOINTS.DEVICES.PERFORMANCE_CONNECTIVITY_PATTERN(deviceId)}?startDate=${startDate}&endDate=${endDate}`, { headers })
    ]);
    
    // Process and combine the data for device modal display
    const connectivity = connectivityData.data;
    const payload = payloadData.data;
    const battery = batteryData.data;
    const rssi = rssiData.data;
    const temperature = temperatureData.data;
    const bootReason = bootReasonData.data;
    const connectivityPattern = connectivityPatternData.data;
    
    const devicePerformance = {
      device: {
        successRate: connectivity.summary?.connectivityPercentage || 0,
        rssiStats: {
          averageRSSI: rssi.summary?.overallAverage || 0,
          signalQuality: rssi.summary?.signalQuality || 'unknown'
        },
        connectivityStats: connectivity.dailyConnectivity || [],
        batteryStats: {
          averageVoltage: battery.summary?.overallAverage || 0,
          totalReadings: battery.summary?.totalReadings || 0,
          dailyBattery: battery.dailyBattery || []
        },
        payloadStats: {
          totalPayloads: payload.summary?.totalPayloads || 0,
          averagePayloads: payload.summary?.averagePayloadsPerDay || 0
        },
        temperatureStats: {
          averageTemperature: temperature.summary?.overallAverage || 0,
          dailyTemperature: temperature.dailyTemperature || []
        },
        bootReasonStats: {
          bootReasons: bootReason.bootReasons || [],
          dailyBootReasons: bootReason.dailyBootReasons || [],
          bootReasonMatrix: bootReason.bootReasonMatrix || [],
          summary: bootReason.summary || {}
        },
        connectivityPattern: {
          hourlyPattern: connectivityPattern.hourlyPattern || []
        }
      },
      summary: {
        connectivityPercentage: connectivity.summary?.connectivityPercentage || 0,
        totalTransmissions: connectivity.summary?.totalTransmissions || 0,
        totalPayloads: payload.summary?.totalPayloads || 0,
        batteryVoltage: battery.summary?.overallAverage || 0,
        rssiAverage: rssi.summary?.overallAverage || 0
      },
      dateRange: { startDate, endDate }
    };
    
    console.log(`✅ Device performance data compiled from individual endpoints:`, devicePerformance);
    return devicePerformance;
  } catch (error) {
    console.error('❌ Error fetching device performance data:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
};

// Fetch log availability for an organization
export const fetchLogAvailability = async (organizationId, startDate, endDate) => {
  console.log(`🌐 fetchLogAvailability called for organization: ${organizationId}`);
  console.log(`🌐 Date range: ${startDate} to ${endDate}`);

  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.LOGS_AVAILABILITY(organizationId)}?startDate=${startDate}&endDate=${endDate}`;
    console.log(`📡 Making HTTP request to: ${url}`);

    const response = await axios.get(url, { headers });
    console.log(`✅ Log availability response received:`, response.status, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching log availability:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
};

// Fetch logs for a specific device on a specific date
export const fetchDeviceLogs = async (organizationId, deviceId, date, page = 1, limit = 100) => {
  console.log(`🌐 fetchDeviceLogs called for device: ${deviceId} on ${date}`);
  console.log(`🌐 Page: ${page}, Limit: ${limit}`);

  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.LOGS_DEVICE(organizationId, deviceId)}?date=${date}&page=${page}&limit=${limit}`;
    console.log(`📡 Making HTTP request to: ${url}`);

    const response = await axios.get(url, { headers });
    console.log(`✅ Device logs response received:`, response.status, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching device logs:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
};

// Export device logs as CSV
export const exportDeviceLogs = async (organizationId, deviceId, date) => {
  console.log(`🌐 exportDeviceLogs called for device: ${deviceId} on ${date}`);

  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.LOGS_DEVICE_EXPORT(organizationId, deviceId)}?date=${date}`;
    console.log(`📡 Making HTTP request to export logs: ${url}`);

    const response = await axios.get(url, {
      headers,
      responseType: 'blob' // Important for file download
    });

    // Create a download link
    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `device_${deviceId}_logs_${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    console.log(`✅ Device logs exported successfully`);
    return true;
  } catch (error) {
    console.error('❌ Error exporting device logs:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
};

// Fetch device report data
export const fetchDeviceReportData = async (deviceId, startDate, endDate, binning = 'daily') => {
  console.log(`🌐 fetchDeviceReportData called for device: ${deviceId}`);
  console.log(`🌐 Date range: ${startDate} to ${endDate}, binning: ${binning}`);

  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}${API_ENDPOINTS.DEVICES.REPORT_DAILY_CONSUMPTION(deviceId)}?startDate=${startDate}&endDate=${endDate}&binning=${binning}`;
    console.log(`📡 Making HTTP request to: ${url}`);

    const response = await axios.get(url, { headers });
    console.log(`✅ Device report response received:`, response.status, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching device report data:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
};

// Fetch documentation files
export const fetchDocumentation = async () => {
  console.log('🌐 fetchDocumentation called');

  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}/documentation/list`;
    console.log(`📡 Making HTTP request to: ${url}`);

    const response = await axios.get(url, { headers });
    console.log(`✅ Documentation response received:`, response.status, response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching documentation:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
};

// Fetch organization sensor configuration (shadow reported + desired values)
export const fetchOrganizationConfiguration = async (organizationId) => {
  console.log(`🌐 fetchOrganizationConfiguration called for organization: ${organizationId}`);

  try {
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.CONFIGURATION(organizationId)}`;
    console.log(`📡 Making HTTP request to: ${url}`);

    const response = await axios.get(url, { headers });
    console.log(`✅ Configuration response received:`, response.status);
    console.log(`📊 Retrieved configuration for ${response.data.totalDevices} devices`);
    return response.data;
  } catch (error) {
    console.error('❌ Error fetching organization configuration:', error);
    if (error.response) {
      console.error('❌ Response status:', error.response.status);
      console.error('❌ Response data:', error.response.data);
    }
    throw error;
  }
}; 
